import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUser,
    faEnvelope,
    faPhone,
    faGraduationCap,
    faPaperPlane,
    faSpinner,
    faArrowLeft,
    faCreditCard,
    faMobile,
    faUniversity,
    faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { toastHelper } from '../../Helpers/toastHelper';
import ApiServices from '../Services/ApiServices';
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import extractErrorMessage from '../../Helpers/APIHelper';

const validationSchema = Yup.object().shape({
    fullName: Yup.string()
        .required("الاسم مطلوب")
        .min(3, "الاسم يجب أن يكون 3 أحرف على الأقل")
        .max(50, "الاسم يجب أن لا يتجاوز 50 حرف"),
    email: Yup.string()
        .required("البريد الإلكتروني مطلوب")
        .email("البريد الإلكتروني غير صالح"),
    phone: Yup.string()
        .required("رقم الهاتف مطلوب")
        .matches(/^[0-9]{11}$/, "رقم الهاتف يجب أن يكون 11 أرقام")
        .test("is-valid-phone", "رقم الهاتف غير صالح", value => {
            if (!value) return false;
            return value.startsWith("01");
        }),
    paymentMethod: Yup.number()
        .required("طريقة الدفع مطلوبة")
        .min(1, "يرجى اختيار طريقة دفع صحيحة")
});

const Register = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(true);

    // Get selected plan ID and details from location state
    const planId = location.state?.planId;
    const packageDetails = location.state?.packageDetails;

    // Fetch payment methods on component mount
    useEffect(() => {
        const fetchPaymentMethods = async () => {
            try {
                setLoadingPaymentMethods(true);
                const response = await ApiServices.get('Lookup/PaymentMethods');
                setPaymentMethods(response);
            } catch (error) {
                
                toastHelper.error('فشل في تحميل طرق الدفع');
            } finally {
                setLoadingPaymentMethods(false);
            }
        };

        fetchPaymentMethods();
    }, []);

    const handleSubmit = async (values, { setSubmitting }) => {
        setLoading(true);
        try {
            const requestData = {
                full_name: values.fullName,
                email: values.email,
                phone: values.phone,
                plan_id: planId,
                payment_method: parseInt(values.paymentMethod)
            };

            const response = await ApiServices.post('Subscription/Request/create', requestData);

            if (response.check) {
                toastHelper.success('تم إرسال طلبك بنجاح، سيتم التواصل معك قريباً');
                navigate('/pricing', {
                    state: { message: 'تم إرسال طلبك بنجاح، سيتم التواصل معك قريباً' }
                });
            } else {
                throw new Error(response.msg || 'فشل في إرسال الطلب');
            }
        } catch (error) {
            const title = error?.title || error?.msg || "";
            if (title.toLowerCase().includes("validation errors")) {
                const errMessage = extractErrorMessage(error);
                toastHelper.error(errMessage);
            } else {
                toastHelper.error(error);
            }
        } finally {
            setLoading(false);
            setSubmitting(false);
        }
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { duration: 0.5 }
        }
    };

    const formVariants = {
        hidden: { y: 50, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                type: "spring",
                stiffness: 100
            }
        }
    };

    // Helper function to get payment method icon
    const getPaymentMethodIcon = (methodId) => {
        switch (methodId) {
            case 1: // فودافون كاش (VodfoneCash)
                return (
                    <div className="vodafone-icon">
                        <svg width="24" height="24" viewBox="-0.398 -4.59 378.918 388.633" xmlns="http://www.w3.org/2000/svg">
                            <path d="m119.441 14.328c47.465-18.918 102.754-17.61 148.954 4.363-13.165-2-26.555-.492-39.586 1.711-35.391 6.684-69.024 23.336-95.313 48.075-25.207 24.777-42.871 57.652-47.86 92.824-3.3 24.922-.241 51.062 10.942 73.746 11.524 23.8 31.809 43.32 56.258 53.394 23.559 9.97 50.887 9.86 74.75.961 35.805-13.14 61.531-48.62 64.473-86.527 1.851-24.844-4.192-51.273-20.883-70.328-15.934-18.652-39.238-28.926-62.559-34.774-1.25-23 9.586-45.722 26.77-60.68 9.543-8.605 21.386-14.09 33.633-17.6l.93-.321c35.058 16.812 64.847 44.539 83.827 78.578 16.262 28.98 24.743 62.375 23.903 95.64-.16 43.016-16.239 85.606-43.801 118.497-26.063 31.367-62.516 53.93-102.246 62.965-39.836 9.191-82.695 5.226-119.977-11.704-36.48-16.293-67.386-44.617-87.047-79.457-16.355-28.953-25.007-62.336-24.289-95.64.078-41.496 14.813-82.672 40.54-115.121 20.55-25.91 47.812-46.512 78.581-58.602zm0 0" fill="#e60000" />
                            <path d="m228.809 20.402c13.03-2.203 26.421-3.71 39.586-1.71l1.89.32-1.265.48c-12.247 3.512-24.09 8.996-33.633 17.602-17.184 14.957-28.02 37.68-26.77 60.68 23.32 5.847 46.625 16.12 62.559 34.773 16.691 19.055 22.734 45.484 20.883 70.328-2.942 37.906-28.668 73.387-64.473 86.527-23.863 8.899-51.191 9.008-74.75-.96-24.45-10.075-44.734-29.594-56.258-53.395-11.183-22.684-14.242-48.824-10.941-73.746 4.988-35.172 22.652-68.047 47.86-92.824 26.288-24.739 59.921-41.391 95.312-48.075zm0 0" fill="#fff" />
                        </svg>
                    </div>
                );
            case 2: // انستا باي (Instapay)
                return (
                    <div className="instapay-icon">
                        <svg width="24" height="24" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
                            <g>
                                <path style={{ opacity: 0.917 }} fill="#502974" d="M 134.5,153.5 C 145.5,153.5 156.5,153.5 167.5,153.5C 164.506,164.982 161.006,176.315 157,187.5C 145.895,188.82 134.728,188.82 123.5,187.5C 127.574,176.277 131.241,164.944 134.5,153.5 Z" />
                            </g>
                            <g>
                                <path style={{ opacity: 0.956 }} fill="#f38270" d="M 174.5,153.5 C 186.171,153.333 197.838,153.5 209.5,154C 221.7,174.704 233.7,195.538 245.5,216.5C 221.583,238.461 197.25,259.961 172.5,281C 160.787,281.187 149.121,281.687 137.5,282.5C 136.289,281.726 136.289,280.893 137.5,280C 162.13,259.039 186.463,237.706 210.5,216C 198.097,195.357 186.097,174.523 174.5,153.5 Z" />
                            </g>
                            <g>
                                <path style={{ opacity: 0.961 }} fill="#ee6723" d="M 293.5,215.5 C 292.797,217.716 291.464,219.549 289.5,221C 280.006,228.663 271.006,236.83 262.5,245.5C 261.833,245.5 261.5,245.833 261.5,246.5C 247.744,257.417 234.41,268.917 221.5,281C 209.787,281.187 198.121,281.687 186.5,282.5C 186.167,282.167 185.833,281.833 185.5,281.5C 210.29,259.881 234.956,238.048 259.5,216C 247.374,195.582 235.374,175.082 223.5,154.5C 235.152,153.334 246.819,153.167 258.5,154C 270.317,174.473 281.984,194.973 293.5,215.5 Z" />
                            </g>
                            <g>
                                <path style={{ opacity: 0.954 }} fill="#7c559e" d="M 261.5,246.5 C 262.167,246.5 262.5,246.167 262.5,245.5C 271.006,236.83 280.006,228.663 289.5,221C 291.464,219.549 292.797,217.716 293.5,215.5C 318.169,215.667 342.836,215.5 367.5,215C 378.2,211.76 383.034,204.594 382,193.5C 380.891,191.185 379.057,189.685 376.5,189C 344.828,188.833 313.161,188.333 281.5,187.5C 291.62,175.879 302.286,164.712 313.5,154C 337.833,153.333 362.167,153.333 386.5,154C 414.906,160.637 424.073,177.803 414,205.5C 401.721,232.233 380.887,247.066 351.5,250C 332.158,250.167 312.825,250.667 293.5,251.5C 290.461,261.45 287.128,271.284 283.5,281C 272.505,281.5 261.505,281.667 250.5,281.5C 253.965,269.773 257.631,258.106 261.5,246.5 Z" />
                            </g>
                            <g>
                                <path style={{ opacity: 0.958 }} fill="#502974" d="M 119.5,200.5 C 130.5,200.5 141.5,200.5 152.5,200.5C 144.039,227.216 135.373,253.882 126.5,280.5C 115.52,281.499 104.52,281.832 93.5,281.5C 101.765,254.372 110.431,227.372 119.5,200.5 Z" />
                            </g>
                            <g>
                                <path style={{ opacity: 0.881 }} fill="#502772" d="M 107.5,312.5 C 111.555,312.176 115.555,312.509 119.5,313.5C 114.223,327.995 109.557,342.662 105.5,357.5C 101.5,357.5 97.5,357.5 93.5,357.5C 97.3618,342.248 102.029,327.248 107.5,312.5 Z" />
                            </g>
                            <g>
                                <path style={{ opacity: 0.908 }} fill="#502772" d="M 164.5,322.5 C 163.662,322.842 163.328,323.508 163.5,324.5C 162.596,325.209 162.263,326.209 162.5,327.5C 159.603,335.688 156.77,344.021 154,352.5C 150.819,358.252 146.819,358.919 142,354.5C 138.06,347.564 133.727,340.897 129,334.5C 126.599,341.871 124.099,349.204 121.5,356.5C 117.555,357.491 113.555,357.824 109.5,357.5C 113.645,342.898 118.478,328.565 124,314.5C 126.243,312.718 128.743,312.218 131.5,313C 137.284,320.065 142.45,327.565 147,335.5C 150.006,327.981 152.506,320.314 154.5,312.5C 158.5,312.5 162.5,312.5 166.5,312.5C 166.041,315.877 165.374,319.211 164.5,322.5 Z" />
                            </g>
                            <g>
                                <path style={{ opacity: 0.893 }} fill="#502671" d="M 278.5,338.5 C 276.989,344.877 275.323,351.21 273.5,357.5C 262.48,357.832 251.48,357.499 240.5,356.5C 243.273,352.223 246.607,348.39 250.5,345C 255.5,344.667 260.5,344.333 265.5,344C 263.805,339.915 262.305,335.748 261,331.5C 253.167,340 245.333,348.5 237.5,357C 228,357.237 218.667,357.07 209.5,356.5C 213.035,346.563 216.368,336.563 219.5,326.5C 214.482,325.786 209.482,325.119 204.5,324.5C 195.196,325.142 185.862,325.809 176.5,326.5C 184.479,327.332 192.479,327.832 200.5,328C 207.057,330.685 209.223,335.519 207,342.5C 204.002,350.164 198.502,354.997 190.5,357C 178.153,357.833 165.82,357.666 153.5,356.5C 156.984,352.515 160.651,348.682 164.5,345C 174.167,344.667 183.833,344.333 193.5,344C 195.394,342.921 195.727,341.587 194.5,340C 185.092,339.918 175.759,339.251 166.5,338C 163.13,335.303 161.796,331.803 162.5,327.5C 163.404,326.791 163.737,325.791 163.5,324.5C 164.338,324.158 164.672,323.492 164.5,322.5C 167.929,317.611 172.596,314.444 178.5,313C 197.471,312.86 216.305,312.693 235,312.5C 241.167,312.833 247.333,313.167 253.5,313.5C 250.016,317.485 246.349,321.318 242.5,325C 238.848,325.499 235.182,325.665 231.5,325.5C 228.74,335.603 225.906,345.603 223,355.5C 234.702,343.467 246.035,331.133 257,318.5C 259.234,316.3 261.734,314.467 264.5,313C 267.257,312.218 269.757,312.718 272,314.5C 274.48,322.42 276.646,330.42 278.5,338.5 Z" />
                            </g>
                            <g>
                                <path style={{ opacity: 0.885 }} fill="#7c559d" d="M 328.5,334.5 C 327.833,334.5 327.5,334.833 327.5,335.5C 326.833,335.5 326.5,335.833 326.5,336.5C 325.833,336.5 325.5,336.833 325.5,337.5C 322.537,340.951 318.87,343.451 314.5,345C 305.872,346.045 297.205,346.878 288.5,347.5C 287.091,350.727 286.091,354.06 285.5,357.5C 281.5,357.5 277.5,357.5 273.5,357.5C 275.323,351.21 276.989,344.877 278.5,338.5C 279.234,337.122 279.901,335.622 280.5,334C 291.833,333.667 303.167,333.333 314.5,333C 317.257,331.181 318.257,328.848 317.5,326C 306.152,325.832 294.819,325.332 283.5,324.5C 286.984,320.515 290.651,316.682 294.5,313C 303.5,312.333 312.5,312.333 321.5,313C 325.499,313.668 328.332,315.835 330,319.5C 330.818,324.651 330.318,329.651 328.5,334.5 Z" />
                            </g>
                            <g>
                                <path style={{ opacity: 0.898 }} fill="#7c569e" d="M 325.5,337.5 C 326.167,337.5 326.5,337.167 326.5,336.5C 327.167,336.5 327.5,336.167 327.5,335.5C 328.167,335.5 328.5,335.167 328.5,334.5C 334.937,327.562 341.603,320.729 348.5,314C 354.213,310.968 358.047,312.468 360,318.5C 363.329,331.482 366.496,344.482 369.5,357.5C 355.151,357.833 340.818,357.499 326.5,356.5C 330.02,352.814 333.353,348.981 336.5,345C 341.5,344.667 346.5,344.333 351.5,344C 350.247,339.827 348.914,335.66 347.5,331.5C 339.345,339.823 331.345,348.323 323.5,357C 318.798,357.828 314.131,357.662 309.5,356.5C 314.879,350.128 320.213,343.795 325.5,337.5 Z" />
                            </g>
                            <g>
                                <path style={{ opacity: 0.898 }} fill="#7c569e" d="M 362.5,312.5 C 367.875,312.172 373.208,312.505 378.5,313.5C 380.836,318.843 383.169,324.176 385.5,329.5C 391.319,324.182 396.985,318.682 402.5,313C 407.533,312.171 412.533,312.338 417.5,313.5C 407.844,322.988 398.011,332.322 388,341.5C 386.068,346.462 384.235,351.462 382.5,356.5C 378.555,357.491 374.555,357.824 370.5,357.5C 371.666,352.17 373.332,347.003 375.5,342C 371.009,332.189 366.675,322.355 362.5,312.5 Z" />
                            </g>
                        </svg>
                    </div>
                );
            case 3: // تحويل بنكي (BankTransfer)
                return <FontAwesomeIcon icon={faUniversity} />;
            default:
                return <FontAwesomeIcon icon={faCreditCard} />;
        }
    };

    // Helper function to get payment method details
    const getPaymentMethodDetails = (methodId) => {
        switch (methodId) {
            case 1: // فودافون كاش (VodfoneCash)
                return "01069903556";
            case 2: // انستا باي (Instapay)
                return "mahmoud.ibrahim74@instapay";
            case 3: // تحويل بنكي (BankTransfer)
                return "رقم الحساب: 100061518902";
            default:
                return "";
        }
    };

    return (
        <section className="modern-register-section">
            <div className="register-background">
                <div className="floating-shapes">
                    <div className="shape shape-1"></div>
                    <div className="shape shape-2"></div>
                    <div className="shape shape-3"></div>
                    <div className="shape shape-4"></div>
                </div>
            </div>

            <motion.button
                className="back-button"
                onClick={() => navigate('/pricing')}
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.95 }}
            >
                <FontAwesomeIcon icon={faArrowLeft} />
                <span></span>
            </motion.button>

            <motion.div
                className="register-container"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                <motion.div
                    className="register-header"
                    initial={{ opacity: 0, y: -30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.7, type: "spring" }}
                >
                    <motion.div
                        className="header-badge"
                        whileHover={{ scale: 1.05 }}
                        transition={{ duration: 0.3 }}
                    >
                        <FontAwesomeIcon icon={faGraduationCap} />
                        <span>انضم إلينا</span>
                    </motion.div>
                    <h1 className="main-title">
                        مرحباً بك في
                        <span className="title-highlight">امتحاني</span>
                    </h1>
                    <p className="main-description">
                        سجّل حسابك الجديد الآن واستمتع بأفضل تجربة تعليمية
                    </p>
                </motion.div>

                <motion.div
                    className="selected-package"
                    initial={{ opacity: 0, scale: 0.9, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.6, type: "spring" }}
                >
                    <div className="package-header">
                        <FontAwesomeIcon icon={faCheckCircle} className="package-icon" />
                        <h3>الباقة المختارة</h3>
                    </div>
                    <div className="package-info">
                        <h4 className="package-name">{packageDetails?.name}</h4>
                        <div className="package-price">
                            <span className="price-amount">{packageDetails?.price}</span>
                            <span className="price-currency">ج.م</span>
                            <span className="price-period">/ {packageDetails?.period_name}</span>
                        </div>
                    </div>
                </motion.div>

                <motion.div
                    className="register-form-container"
                    variants={formVariants}
                >
                    <Formik
                        initialValues={{
                            fullName: '',
                            email: '',
                            phone: '',
                            paymentMethod: ''
                        }}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                    >
                        {({ isSubmitting, errors, touched, values, setFieldValue }) => (
                            <Form className="register-form">
                                <motion.div
                                    className="form-group"
                                    initial={{ opacity: 0, x: -30 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.4, duration: 0.6 }}
                                >
                                    <label className="form-label">الاسم بالكامل</label>
                                    <div className="input-wrapper">
                                        <FontAwesomeIcon icon={faUser} className="input-icon" />
                                        <Field
                                            type="text"
                                            name="fullName"
                                            placeholder="أدخل اسمك الكامل"
                                            className={`form-control ${errors.fullName && touched.fullName ? 'error' : ''}`}
                                        />
                                    </div>
                                    <ErrorMessage name="fullName" component="div" className="error-message" />
                                </motion.div>

                                <motion.div
                                    className="form-group"
                                    initial={{ opacity: 0, x: -30 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.5, duration: 0.6 }}
                                >
                                    <label className="form-label">البريد الإلكتروني</label>
                                    <div className="input-wrapper">
                                        <FontAwesomeIcon icon={faEnvelope} className="input-icon" />
                                        <Field
                                            type="email"
                                            name="email"
                                            placeholder="أدخل بريدك الإلكتروني"
                                            className={`form-control ${errors.email && touched.email ? 'error' : ''}`}
                                        />
                                    </div>
                                    <ErrorMessage name="email" component="div" className="error-message" />
                                </motion.div>

                                <motion.div
                                    className="form-group"
                                    initial={{ opacity: 0, x: -30 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.6, duration: 0.6 }}
                                >
                                    <label className="form-label">رقم الهاتف</label>
                                    <div className="input-wrapper">
                                        <FontAwesomeIcon icon={faPhone} className="input-icon" />
                                        <Field
                                            type="tel"
                                            name="phone"
                                            placeholder="01xxxxxxxxx"
                                            className={`form-control ${errors.phone && touched.phone ? 'error' : ''}`}
                                        />
                                    </div>
                                    <ErrorMessage name="phone" component="div" className="error-message" />
                                </motion.div>

                                {/* Payment Methods Selection */}
                                <motion.div
                                    className="form-group"
                                    initial={{ opacity: 0, x: -30 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.7, duration: 0.6 }}
                                >
                                    <label className={`form-label payment-label ${errors.paymentMethod && touched.paymentMethod ? 'error' : ''}`}>
                                        <FontAwesomeIcon icon={faCreditCard} />
                                        <span>طريقة الدفع</span>
                                    </label>
                                    {loadingPaymentMethods ? (
                                        <div className="payment-loading">
                                            <FontAwesomeIcon icon={faSpinner} spin />
                                            <span>جاري تحميل طرق الدفع...</span>
                                        </div>
                                    ) : (
                                        <div className={`payment-methods-grid ${errors.paymentMethod && touched.paymentMethod ? 'error' : ''}`}>
                                            {paymentMethods.map((method) => (
                                                <motion.label
                                                    key={method.id}
                                                    className="payment-option"
                                                    whileHover={{ scale: 1.02 }}
                                                    whileTap={{ scale: 0.98 }}
                                                >
                                                    <Field
                                                        type="radio"
                                                        name="paymentMethod"
                                                        value={method.id}
                                                        className="payment-radio"
                                                        onClick={() => setFieldValue('paymentMethod', method.id)}
                                                    />
                                                    <div className={`payment-card ${values.paymentMethod == method.id ? 'selected' : ''}`}>
                                                        <div className="payment-icon">
                                                            {getPaymentMethodIcon(method.id)}
                                                        </div>
                                                        <div className="payment-info">
                                                            <h4 className="payment-name">{method.name}</h4>
                                                            <p className="payment-details">
                                                                {getPaymentMethodDetails(method.id)}
                                                            </p>
                                                        </div>
                                                        <div className={`payment-check ${values.paymentMethod == method.id ? 'selected' : ''}`}>
                                                            <FontAwesomeIcon icon={faCheckCircle} />
                                                        </div>
                                                    </div>
                                                </motion.label>
                                            ))}
                                        </div>
                                    )}
                                    <ErrorMessage name="paymentMethod" component="div" className="error-message" />
                                </motion.div>

                                <motion.button
                                    type="submit"
                                    className="submit-button"
                                    whileHover={{ scale: 1.05, y: -2 }}
                                    whileTap={{ scale: 0.95 }}
                                    disabled={isSubmitting || loading}
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.8, duration: 0.6 }}
                                >
                                    {loading ? (
                                        <>
                                            <FontAwesomeIcon icon={faSpinner} spin />
                                            <span>جاري التسجيل...</span>
                                        </>
                                    ) : (
                                        <>
                                            <span>سجّل الآن</span>
                                            <FontAwesomeIcon icon={faPaperPlane} />
                                        </>
                                    )}
                                </motion.button>
                            </Form>
                        )}
                    </Formik>
                </motion.div>
            </motion.div>

            <style jsx>{`
                * {
                    box-sizing: border-box;
                }

                .modern-register-section {
                    min-height: 100vh;
                    width: 100%;
                    max-width: 100%;
                    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-family: 'Cairo', 'Tajawal', Arial, sans-serif;
                    position: relative;
                    padding: 20px;
                    overflow-x: hidden;
                }

                .register-background {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    z-index: 1;
                }

                .floating-shapes {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                }

                .shape {
                    position: absolute;
                    border-radius: 50%;
                    background: rgba(102, 126, 234, 0.1);
                    animation: float 6s ease-in-out infinite;
                }

                .shape-1 {
                    width: 80px;
                    height: 80px;
                    top: 15%;
                    left: 10%;
                    animation-delay: 0s;
                }

                .shape-2 {
                    width: 120px;
                    height: 120px;
                    top: 70%;
                    right: 15%;
                    animation-delay: 2s;
                }

                .shape-3 {
                    width: 60px;
                    height: 60px;
                    top: 85%;
                    left: 20%;
                    animation-delay: 4s;
                }

                .shape-4 {
                    width: 100px;
                    height: 100px;
                    top: 25%;
                    right: 25%;
                    animation-delay: 1s;
                }

                @keyframes float {
                    0%, 100% { transform: translateY(0px) rotate(0deg); }
                    50% { transform: translateY(-15px) rotate(180deg); }
                }

                .back-button {
                    position: absolute;
                    top: 30px;
                    left: 30px;
                    background: rgba(255, 255, 255, 0.9);
                    border: 2px solid rgba(102, 126, 234, 0.2);
                    border-radius: 50px;
                    padding: 12px 20px;
                    color: #667eea;
                    font-size: 1rem;
                    font-weight: 600;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    box-shadow: 0 8px 32px rgba(102, 126, 234, 0.1);
                    transition: all 0.3s ease;
                    backdrop-filter: blur(20px);
                    z-index: 10;
                    cursor: pointer;
                }

                .back-button:hover {
                    background: #667eea;
                    color: white;
                    transform: translateY(-2px);
                    box-shadow: 0 12px 40px rgba(102, 126, 234, 0.2);
                }

                .register-container {
                    background: rgba(255, 255, 255, 0.95);
                    border-radius: 24px;
                    padding: 50px 40px;
                    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
                    max-width: 600px;
                    width: 100%;
                    border: 2px solid rgba(255, 255, 255, 0.2);
                    backdrop-filter: blur(20px);
                    position: relative;
                    z-index: 2;
                }

                .register-header {
                    text-align: center;
                    margin-bottom: 40px;
                }

                .header-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    padding: 12px 24px;
                    border-radius: 50px;
                    font-size: 0.9rem;
                    font-weight: 600;
                    margin-bottom: 24px;
                    box-shadow: 0 8px 32px rgba(102, 126, 234, 0.3);
                }

                .main-title {
                    font-size: 2.5rem;
                    font-weight: 800;
                    color: #2d3748;
                    margin-bottom: 16px;
                    line-height: 1.2;
                }

                .title-highlight {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                }

                .main-description {
                    font-size: 1.1rem;
                    color: #718096;
                    max-width: 400px;
                    margin: 0 auto;
                    line-height: 1.6;
                }

                .selected-package {
                    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
                    border: 2px solid rgba(102, 126, 234, 0.1);
                    border-radius: 20px;
                    padding: 25px;
                    margin-bottom: 40px;
                    position: relative;
                    overflow: hidden;
                }

                .package-header {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    margin-bottom: 15px;
                }

                .package-icon {
                    color: #48bb78;
                    font-size: 1.2rem;
                }

                .package-header h3 {
                    margin: 0;
                    font-size: 1.1rem;
                    font-weight: 700;
                    color: #2d3748;
                }

                .package-info {
                    text-align: center;
                }

                .package-name {
                    margin: 0 0 12px 0;
                    font-size: 1.4rem;
                    font-weight: 700;
                    color: #667eea;
                }

                .package-price {
                    display: flex;
                    align-items: baseline;
                    justify-content: center;
                    gap: 8px;
                }

                .price-amount {
                    font-size: 2rem;
                    font-weight: 800;
                    color: #2d3748;
                }

                .price-currency {
                    font-size: 1.2rem;
                    font-weight: 600;
                    color: #718096;
                }

                .price-period {
                    font-size: 1rem;
                    color: #718096;
                    font-weight: 500;
                }

                .register-form-container {
                    position: relative;
                }

                .register-form {
                    display: flex;
                    flex-direction: column;
                    gap: 25px;
                }

                .form-group {
                    display: flex;
                    flex-direction: column;
                }

                .form-label {
                    font-size: 1rem;
                    font-weight: 700;
                    color: #2d3748;
                    margin-bottom: 8px;
                    text-align: right;
                }

                .payment-label {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .input-wrapper {
                    position: relative;
                }

                .input-icon {
                    position: absolute;
                    right: 18px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #718096;
                    font-size: 1.1rem;
                    z-index: 2;
                }

                .form-control {
                    width: 100%;
                    padding: 18px 50px 18px 18px;
                    border: 2px solid #e2e8f0;
                    border-radius: 16px;
                    font-size: 1rem;
                    font-family: inherit;
                    background: rgba(255, 255, 255, 0.8);
                    color: #2d3748;
                    transition: all 0.3s ease;
                    box-sizing: border-box;
                    backdrop-filter: blur(10px);
                }

                .form-control:focus {
                    outline: none;
                    border-color: #667eea;
                    background: rgba(255, 255, 255, 0.95);
                    box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
                }

                .form-control.error {
                    border-color: #f56565;
                    background: rgba(254, 242, 242, 0.8);
                }

                .error-message {
                    color: #f56565;
                    font-size: 0.875rem;
                    margin-top: 8px;
                    text-align: right;
                    font-weight: 500;
                }

                .payment-loading {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 12px;
                    padding: 30px;
                    color: #718096;
                    font-size: 1rem;
                    background: rgba(255, 255, 255, 0.5);
                    border-radius: 16px;
                    border: 2px dashed #e2e8f0;
                }

                .payment-methods-grid {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 16px;
                    transition: all 0.3s ease;
                }

                .payment-methods-grid.error {
                    border: 2px solid #f56565;
                    border-radius: 16px;
                    padding: 16px;
                    background: rgba(254, 242, 242, 0.3);
                }

                .payment-option {
                    display: block;
                    cursor: pointer;
                }

                .payment-radio {
                    display: none;
                }

                .payment-card {
                    display: flex;
                    align-items: center;
                    padding: 20px;
                    background: rgba(255, 255, 255, 0.8);
                    border: 2px solid #e2e8f0;
                    border-radius: 16px;
                    transition: all 0.3s ease;
                    gap: 16px;
                    backdrop-filter: blur(10px);
                }

                .payment-option:hover .payment-card {
                    border-color: #667eea;
                    background: rgba(255, 255, 255, 0.95);
                    box-shadow: 0 8px 32px rgba(102, 126, 234, 0.1);
                }

                .payment-radio:checked + .payment-card,
                .payment-card.selected {
                    border-color: #667eea;
                    background: rgba(102, 126, 234, 0.05);
                    box-shadow: 0 8px 32px rgba(102, 126, 234, 0.15);
                }

                .payment-icon {
                    width: 50px;
                    height: 50px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: rgba(102, 126, 234, 0.1);
                    border-radius: 12px;
                    color: #667eea;
                    font-size: 1.3rem;
                    flex-shrink: 0;
                }

                .vodafone-icon {
                    width: 50px;
                    height: 50px;
                    background: #fff;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 6px;
                    box-shadow: 0 4px 12px rgba(230, 0, 18, 0.15);
                }

                .vodafone-icon svg {
                    width: 100%;
                    height: 100%;
                }

                .instapay-icon {
                    width: 50px;
                    height: 50px;
                    background: #fff;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 6px;
                    box-shadow: 0 4px 12px rgba(80, 41, 116, 0.15);
                }

                .instapay-icon svg {
                    width: 100%;
                    height: 100%;
                }

                .payment-info {
                    flex: 1;
                    text-align: right;
                }

                .payment-name {
                    font-size: 1.1rem;
                    font-weight: 700;
                    color: #2d3748;
                    margin: 0 0 6px 0;
                }

                .payment-details {
                    font-size: 0.9rem;
                    color: #718096;
                    font-weight: 500;
                    margin: 0;
                }

                .payment-check {
                    width: 28px;
                    height: 28px;
                    border: 2px solid #e2e8f0;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #fff;
                    color: transparent;
                    font-size: 0.9rem;
                    transition: all 0.3s ease;
                    flex-shrink: 0;
                }

                .payment-check svg {
                    display: none;
                    color: #fff;
                }

                .payment-check.selected {
                    background: #667eea !important;
                    color: #fff !important;
                    border-color: #667eea !important;
                }

                .payment-check.selected svg {
                    display: block !important;
                    color: #fff !important;
                }

                .submit-button {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    border: none;
                    border-radius: 16px;
                    padding: 18px 32px;
                    font-size: 1.1rem;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    box-shadow: 0 8px 32px rgba(102, 126, 234, 0.3);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 12px;
                    margin-top: 20px;
                }

                .submit-button:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 40px rgba(102, 126, 234, 0.4);
                }

                .submit-button:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                    transform: none;
                }

                /* Responsive Design */
                @media (max-width: 768px) {
                    .register-container {
                        padding: 40px 25px;
                        margin: 20px;
                    }

                    .main-title {
                        font-size: 2rem;
                    }

                    .form-control {
                        padding: 16px 45px 16px 16px;
                        font-size: 0.95rem;
                    }

                    .payment-card {
                        padding: 16px;
                        gap: 12px;
                    }

                    .payment-icon {
                        width: 45px;
                        height: 45px;
                        font-size: 1.2rem;
                    }

                    .vodafone-icon,
                    .instapay-icon {
                        width: 45px;
                        height: 45px;
                        padding: 5px;
                    }

                    .payment-name {
                        font-size: 1rem;
                    }

                    .payment-details {
                        font-size: 0.85rem;
                    }

                    .submit-button {
                        padding: 16px 28px;
                        font-size: 1rem;
                    }
                }

                @media (max-width: 480px) {
                    .register-container {
                        padding: 30px 20px;
                        margin: 15px;
                    }

                    .main-title {
                        font-size: 1.8rem;
                    }

                    .form-control {
                        padding: 14px 40px 14px 14px;
                        font-size: 0.9rem;
                    }

                    .payment-card {
                        padding: 14px;
                        gap: 10px;
                    }

                    .payment-icon {
                        width: 40px;
                        height: 40px;
                        font-size: 1.1rem;
                    }

                    .vodafone-icon,
                    .instapay-icon {
                        width: 40px;
                        height: 40px;
                        padding: 4px;
                    }

                    .payment-name {
                        font-size: 0.95rem;
                    }

                    .payment-details {
                        font-size: 0.8rem;
                    }

                    .submit-button {
                        padding: 14px 24px;
                        font-size: 0.95rem;
                    }
                }

                @media (max-width: 360px) {
                    .register-container {
                        padding: 25px 15px;
                        margin: 10px;
                    }

                    .main-title {
                        font-size: 1.6rem;
                    }

                    .form-control {
                        padding: 12px 35px 12px 12px;
                        font-size: 0.85rem;
                    }

                    .payment-card {
                        padding: 12px;
                        gap: 8px;
                    }

                    .payment-icon {
                        width: 35px;
                        height: 35px;
                        font-size: 1rem;
                    }

                    .vodafone-icon,
                    .instapay-icon {
                        width: 35px;
                        height: 35px;
                        padding: 3px;
                    }
                }
            `}</style>
        </section>
    );
};

export default Register; 